import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import BookMyOpAPI from '../services/BookMyOpapi';
import { useBookMyOpAuth } from './BookMyOpAuthContext';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const { user, isAuthenticated } = useBookMyOpAuth();
  const [favoriteDoctorIds, setFavoriteDoctorIds] = useState(new Set());
  const [favoriteHospitalIds, setFavoriteHospitalIds] = useState(new Set());
  const [loading, setLoading] = useState(false);

  // Fetch all favorites
  const fetchFavorites = useCallback(async () => {
    if (!isAuthenticated) {
      setFavoriteDoctorIds(new Set());
      setFavoriteHospitalIds(new Set());
      return;
    }

    try {
      setLoading(true);
      console.log('🔖 Fetching all favorites...');

      // Fetch both doctors and hospitals in parallel
      const [doctorsResponse, hospitalsResponse] = await Promise.allSettled([
        BookMyOpAPI.getFavoriteDoctors(),
        BookMyOpAPI.getFavoriteProviders()
      ]);

      // Handle doctors response
      if (doctorsResponse.status === 'fulfilled') {
        const doctorsData = doctorsResponse.value;
        console.log('📦 Raw doctors response:', doctorsData);
        
        // Extract doctors from the response - handle multiple structures
        let doctors = [];
        if (Array.isArray(doctorsData)) {
          doctors = doctorsData;
        } else if (doctorsData.favorites && Array.isArray(doctorsData.favorites)) {
          // API returns { favorites: [...], count: X }
          doctors = doctorsData.favorites;
        } else if (doctorsData.results && Array.isArray(doctorsData.results)) {
          doctors = doctorsData.results;
        } else if (doctorsData.data) {
          if (Array.isArray(doctorsData.data)) {
            doctors = doctorsData.data;
          } else if (doctorsData.data.favorites && Array.isArray(doctorsData.data.favorites)) {
            doctors = doctorsData.data.favorites;
          } else if (doctorsData.data.results && Array.isArray(doctorsData.data.results)) {
            doctors = doctorsData.data.results;
          }
        }

        console.log('📋 Extracted doctors array:', doctors);

        // Extract doctor IDs from favorites
        const doctorIds = doctors
          .filter(item => item.favorite_type === 'doctor' && item.doctor)
          .map(item => item.doctor.id)
          .filter(id => id !== null && id !== undefined);

        setFavoriteDoctorIds(new Set(doctorIds));
        console.log('✅ Favorite doctor IDs loaded:', doctorIds);
      }

      // Handle hospitals response
      if (hospitalsResponse.status === 'fulfilled') {
        const hospitalsData = hospitalsResponse.value;
        console.log('📦 Raw hospitals response:', hospitalsData);
        
        // Extract hospitals from the response - handle multiple structures
        let hospitals = [];
        if (Array.isArray(hospitalsData)) {
          hospitals = hospitalsData;
        } else if (hospitalsData.favorites && Array.isArray(hospitalsData.favorites)) {
          // API returns { favorites: [...], count: X }
          hospitals = hospitalsData.favorites;
        } else if (hospitalsData.results && Array.isArray(hospitalsData.results)) {
          hospitals = hospitalsData.results;
        } else if (hospitalsData.data) {
          if (Array.isArray(hospitalsData.data)) {
            hospitals = hospitalsData.data;
          } else if (hospitalsData.data.favorites && Array.isArray(hospitalsData.data.favorites)) {
            hospitals = hospitalsData.data.favorites;
          } else if (hospitalsData.data.results && Array.isArray(hospitalsData.data.results)) {
            hospitals = hospitalsData.data.results;
          }
        }

        console.log('📋 Extracted hospitals array:', hospitals);

        // Extract hospital IDs from favorites
        const hospitalIds = hospitals
          .filter(item => item.favorite_type === 'service_provider' && item.service_provider)
          .map(item => item.service_provider.id)
          .filter(id => id !== null && id !== undefined);

        setFavoriteHospitalIds(new Set(hospitalIds));
        console.log('✅ Favorite hospital IDs loaded:', hospitalIds);
      }
    } catch (error) {
      console.error('❌ Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch favorites on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchFavorites();
    } else {
      setFavoriteDoctorIds(new Set());
      setFavoriteHospitalIds(new Set());
    }
  }, [user, fetchFavorites]);

  // Listen for favorites updates
  useEffect(() => {
    const handleFavoritesUpdate = () => {
      console.log('🔄 Favorites updated, refreshing...');
      fetchFavorites();
    };

    window.addEventListener('favoritesUpdated', handleFavoritesUpdate);
    return () => window.removeEventListener('favoritesUpdated', handleFavoritesUpdate);
  }, [fetchFavorites]);

  // Helper function to check if a doctor is favorited
  const isDoctorFavorite = useCallback((doctorId) => {
    return favoriteDoctorIds.has(doctorId);
  }, [favoriteDoctorIds]);

  // Helper function to check if a hospital is favorited
  const isHospitalFavorite = useCallback((hospitalId) => {
    return favoriteHospitalIds.has(hospitalId);
  }, [favoriteHospitalIds]);

  const value = {
    favoriteDoctorIds,
    favoriteHospitalIds,
    loading,
    isDoctorFavorite,
    isHospitalFavorite,
    refreshFavorites: fetchFavorites,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

